import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useInView } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowRight, TrendingDown, MapPin, Landmark, CheckCircle2, Phone, Calculator, IndianRupee } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/887722b5-7ad9-4cfc-8366-fffd7eed17f9/images/a31d2b3f6b1cb7255440086e3b828476059e068d6fff380dcfe63960cbcf7f93.jpeg";

const rows = [
  { hub: "IIE Sigaddi, Kotdwar", price: "₹7,500", ncr: "SIIDCUL / SIIDCUL · 90-yr lease · SIDA approved", highlight: true },
  { hub: "IMT Kharkhoda (Sonipat NCR)", price: "₹39,200 – ₹60,000", ncr: "HSIIDC allotment / resale" },
  { hub: "Tronica City (Ghaziabad NCR)", price: "₹35,000 – ₹45,000 /sq.yd", ncr: "UPSIDA developed" },
  { hub: "Meerut IMLC (UPEIDA)", price: "₹11,470+", ncr: "Ganga Expressway node" },
  { hub: "Haridwar / Roorkee estates", price: "₹22,500 /sq.yd", ncr: "Private estates near highway" },
];

const reasons = [
  { icon: TrendingDown, title: "5-8x Lower Land Cost", text: "At a flat ₹7,500/sq.mtr, Kotdwar land costs a fraction of Delhi-NCR hubs — freeing capital for plant, machinery and working capital." },
  { icon: MapPin, title: "Same North India Reach", text: "Rail + road links to Delhi-NCR, Dehradun and Haridwar, plus the improving Delhi–Dehradun corridor, keep you close to markets and suppliers." },
  { icon: Landmark, title: "Stronger State Incentives", text: "Uttarakhand Industrial & Logistics Policy benefits — capital subsidy, SGST reimbursement and stamp-duty exemption — that NCR states don't match." },
];

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

const NCR_BENCHMARKS = [
  { label: "IMT Kharkhoda (Sonipat NCR)", rate: 45000 },
  { label: "Tronica City (Ghaziabad NCR)", rate: 40000 },
  { label: "Haridwar / Roorkee estates", rate: 25000 },
  { label: "Meerut IMLC (UPEIDA)", rate: 12000 },
];

function fmtINR(a) {
  if (a >= 1e7) return `₹${(a / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(a / 1e5).toFixed(2)} L`;
  return `₹${Math.round(a).toLocaleString("en-IN")}`;
}

function SavingsCalculator() {
  const [size, setSize] = useState(2000);
  const [benchIdx, setBenchIdx] = useState(0);
  const bench = NCR_BENCHMARKS[benchIdx];
  const kotdwar = size * 7500;
  const ncr = size * bench.rate;
  const savings = Math.max(ncr - kotdwar, 0);
  const pct = ncr ? Math.round((savings / ncr) * 100) : 0;

  return (
    <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="savings-calculator">
      {/* Inputs */}
      <div className="lg:col-span-2 bg-[#1E293B] border border-white/[0.08] p-7">
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Plot Size — <span className="text-orange-400 font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{size.toLocaleString("en-IN")} sq.mtr</span>
          </label>
          <input
            type="range"
            min={500}
            max={10000}
            step={100}
            value={size}
            onChange={(e) => setSize(Number(e.target.value))}
            className="w-full accent-orange-500"
            data-testid="savings-size-slider"
          />
          <div className="flex justify-between text-xs text-slate-500 mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            <span>500</span><span>10,000</span>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Compare Against (Delhi-NCR benchmark)</label>
          <select
            value={benchIdx}
            onChange={(e) => setBenchIdx(Number(e.target.value))}
            className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white"
            data-testid="savings-benchmark-select"
          >
            {NCR_BENCHMARKS.map((b, i) => {
              const optLabel = `${b.label} — ₹${b.rate.toLocaleString("en-IN")}/sq.mtr`;
              return <option key={i} value={i}>{optLabel}</option>;
            })}
          </select>
        </div>
      </div>

      {/* Results */}
      <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-slate-900/60 border border-orange-500/40 p-6 flex flex-col justify-center">
          <div className="text-slate-400 text-xs uppercase tracking-wider" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Kotdwar Cost</div>
          <div className="text-orange-400 font-black mt-1" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "1.9rem" }} data-testid="savings-kotdwar">{fmtINR(kotdwar)}</div>
          <div className="text-slate-500 text-xs mt-1">@ ₹7,500 / sq.mtr</div>
        </div>
        <div className="bg-slate-900/60 border border-white/[0.08] p-6 flex flex-col justify-center">
          <div className="text-slate-400 text-xs uppercase tracking-wider" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Delhi-NCR Cost</div>
          <div className="text-slate-200 font-black mt-1" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "1.9rem" }} data-testid="savings-ncr">{fmtINR(ncr)}</div>
          <div className="text-slate-500 text-xs mt-1">@ ₹{bench.rate.toLocaleString("en-IN")} / sq.mtr</div>
        </div>
        <div className="sm:col-span-2 bg-gradient-to-r from-emerald-500/15 to-emerald-500/5 border border-emerald-500/40 p-6 flex items-center justify-between">
          <div>
            <div className="text-emerald-300 text-xs uppercase tracking-wider flex items-center gap-1.5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              <TrendingDown className="w-3.5 h-3.5" /> You Save vs Delhi-NCR
            </div>
            <div className="text-emerald-400 font-black mt-1" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "2.4rem", lineHeight: 1 }} data-testid="savings-total">{fmtINR(savings)}</div>
          </div>
          <div className="text-right">
            <div className="text-emerald-400 font-black" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "3rem", lineHeight: 1 }} data-testid="savings-percent">{pct}%</div>
            <div className="text-emerald-300/70 text-xs uppercase tracking-wider">lower</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const CANON = "https://vsindlpark.in/kotdwar-vs-delhi-ncr";

export default function ComparisonPage() {
  return (
    <div className="bg-[#0F172A]" data-testid="comparison-page">
      <Helmet>
        <title>Kotdwar vs Delhi NCR Industrial Land — Price Comparison | VSINDLPARK.COM</title>
        <meta name="description" content="Industrial plot price comparison: Kotdwar (IIE Sigaddi, SIIDCUL/SIIDCUL) at a flat ₹7,500/sq.mtr vs Delhi NCR hubs like IMT Kharkhoda, Tronica City and Meerut at ₹11,000-60,000/sq.mtr. See why Kotdwar delivers far higher ROI." />
        <meta name="keywords" content="kotdwar vs delhi ncr industrial land, industrial plot price comparison uttarakhand, cheap factory land near delhi, SIIDCUL kotdwar vs kharkhoda, industrial land price per sq meter uttarakhand" />
        <link rel="canonical" href={CANON} />
        <meta property="og:title" content="Kotdwar vs Delhi NCR Industrial Land — Price Comparison" />
        <meta property="og:description" content="Kotdwar industrial plots at ₹7,500/sq.mtr vs ₹11,000-60,000 in Delhi NCR. Same North India reach, stronger Uttarakhand incentives." />
        <meta property="og:url" content={CANON} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://vsindlpark.in" },
            { "@type": "ListItem", position: 2, name: "Kotdwar vs Delhi NCR", item: CANON },
          ],
        })}</script>
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="relative h-[70vh] min-h-[480px] overflow-hidden" data-testid="comparison-hero">
        <img src={HERO} alt="Aerial view of Kotdwar industrial park vs Delhi NCR" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/70 to-slate-950/40" />
        <div className="absolute inset-0 blueprint-grid opacity-25 pointer-events-none" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <Reveal className="max-w-3xl">
              <span className="overline inline-flex items-center gap-2 border border-orange-500/30 px-3 py-1.5">
                <TrendingDown className="w-3.5 h-3.5 text-orange-500" /> Price Comparison
              </span>
              <h1 className="text-white mt-5" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: "clamp(2.5rem, 5.5vw, 4.5rem)", lineHeight: 0.95, textTransform: "uppercase", letterSpacing: "-0.03em" }}>
                Kotdwar vs Delhi NCR<br />Industrial Land
              </h1>
              <p className="text-slate-300 text-base md:text-lg mt-5 max-w-2xl leading-relaxed">
                Why pay ₹40,000+ per sq.mtr near Delhi when the SIIDCUL Sigaddi Growth Centre in Kotdwar offers SIDA-approved industrial plots at a flat <strong className="text-orange-400">₹7,500/sq.mtr</strong> — with the same North India connectivity and stronger Uttarakhand incentives.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/plots" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 font-bold uppercase tracking-wider text-sm transition-all duration-300 flex items-center gap-2 hover:shadow-[0_0_30px_rgba(249,115,22,0.4)]" data-testid="comparison-view-plots-btn">
                  View Available Plots <ArrowRight className="w-4 h-4" />
                </Link>
                <a href="tel:+919258959058" className="border border-white/30 text-white hover:bg-white/10 px-8 py-3 font-bold uppercase tracking-wider text-sm transition-all duration-300 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> +91-9258959058
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="relative py-24 border-y border-white/10" data-testid="comparison-table-section">
        <div className="absolute inset-0 blueprint-grid opacity-10 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Reveal>
            <span className="overline">The Numbers</span>
            <div className="section-divider-left mt-3" />
            <h2 className="text-white mt-4" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: "clamp(2rem, 3vw, 2.8rem)", textTransform: "uppercase", lineHeight: 1.1 }}>
              Industrial Plot Price Per Sq.Mtr
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 overflow-x-auto border border-white/[0.08]">
              <table className="w-full text-left" data-testid="comparison-price-table">
                <thead>
                  <tr className="bg-slate-900/70 text-slate-400 text-xs uppercase tracking-wider" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    <th className="px-5 py-4 font-medium">Location</th>
                    <th className="px-5 py-4 font-medium">Price / sq.mtr</th>
                    <th className="px-5 py-4 font-medium hidden md:table-cell">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {rows.map((r, i) => (
                    <tr key={i} className={r.highlight ? "bg-orange-500/[0.07]" : "hover:bg-white/[0.02] transition-colors"} data-testid={`comparison-row-${i}`}>
                      <td className="px-5 py-4">
                        <span className={`text-sm font-semibold ${r.highlight ? "text-orange-400" : "text-slate-200"}`}>{r.hub}</span>
                      </td>
                      <td className={`px-5 py-4 text-sm font-bold ${r.highlight ? "text-orange-400" : "text-slate-300"}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{r.price}</td>
                      <td className="px-5 py-4 text-xs text-slate-500 hidden md:table-cell">{r.ncr}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-slate-600 text-xs mt-3" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              * Indicative market ranges for comparison. VSINDLPARK plots are offered at a flat ₹7,500/sq.mtr.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Reasons */}
      <section className="relative py-24" data-testid="comparison-reasons-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <span className="overline">Why It Wins</span>
            <div className="section-divider-left mt-3" />
            <h2 className="text-white mt-4" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: "clamp(2rem, 3vw, 2.8rem)", textTransform: "uppercase", lineHeight: 1.1 }}>
              3 Reasons Kotdwar Beats NCR
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {reasons.map((r, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="bg-[#1E293B] border border-white/[0.08] p-7 h-full hover:border-orange-500/40 transition-colors" data-testid={`comparison-reason-${i}`}>
                  <div className="w-11 h-11 flex items-center justify-center bg-orange-500/10 border border-orange-500/30 mb-5">
                    <r.icon className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-white text-lg font-bold mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: "uppercase", letterSpacing: "0.02em" }}>{r.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Savings Calculator */}
      <section className="relative py-24 border-t border-white/10 bg-slate-900/40" data-testid="savings-section">
        <div className="absolute inset-0 blueprint-grid opacity-10 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Reveal>
            <span className="overline inline-flex items-center gap-2">
              <Calculator className="w-3.5 h-3.5 text-orange-500" /> Savings Calculator
            </span>
            <div className="section-divider-left mt-3" />
            <h2 className="text-white mt-4" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: "clamp(2rem, 3vw, 2.8rem)", textTransform: "uppercase", lineHeight: 1.1 }}>
              How Much You Save vs Delhi-NCR
            </h2>
            <p className="text-slate-400 text-sm mt-3 max-w-2xl leading-relaxed">
              Drag the slider to your plot size and pick a Delhi-NCR benchmark to see your instant land-cost saving at a flat ₹7,500/sq.mtr in Kotdwar.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <SavingsCalculator />
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/booking" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 font-bold uppercase tracking-wider text-sm transition-all duration-300 hover:shadow-[0_0_30px_rgba(249,115,22,0.4)] flex items-center gap-2" data-testid="savings-book-btn">
                Book a Plot & Save <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="tel:+919258959058" className="border border-white/30 text-white hover:bg-white/10 px-8 py-3 font-bold uppercase tracking-wider text-sm transition-all duration-300 flex items-center gap-2">
                <IndianRupee className="w-4 h-4" /> Discuss Your Numbers
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 border-t border-white/10 bg-slate-900/40" data-testid="comparison-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-white mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: "clamp(2rem, 4vw, 3rem)", textTransform: "uppercase", lineHeight: 1.05 }}>
              Lock In ₹7,500/sq.mtr Today
            </h2>
            <p className="text-slate-400 text-sm mb-8 max-w-xl mx-auto leading-relaxed">
              Speak to our team about available plots at IIE Sigaddi, Kotdwar and how much you could save versus a Delhi-NCR setup.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/booking" className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-3.5 font-bold uppercase tracking-wider text-sm transition-all duration-300 hover:shadow-[0_0_30px_rgba(249,115,22,0.5)] flex items-center gap-2" data-testid="comparison-book-btn">
                Book a Plot <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/schemes" className="border border-white/30 text-white hover:bg-white/10 px-10 py-3.5 font-bold uppercase tracking-wider text-sm transition-all duration-300 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> View Incentives
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
